/**
 * Nested overflow:auto (even when it cannot scroll) swallows wheel/trackpad
 * in Chrome. Dragging the parent scrollbar still works. Capture wheel on the
 * intended scroller and apply delta when no inner pane can actually scroll.
 */
import { useCallback, useRef } from "react";

function overflowAllowsScroll(value: string): boolean {
  return value === "auto" || value === "scroll" || value === "overlay";
}

function canScrollAlong(
  el: HTMLElement,
  deltaY: number,
  deltaX: number,
): boolean {
  const style = window.getComputedStyle(el);
  const vertical = Math.abs(deltaY) >= Math.abs(deltaX);
  if (vertical) {
    if (!overflowAllowsScroll(style.overflowY)) return false;
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 1) return false;
    if (deltaY < 0 && el.scrollTop <= 0) return false;
    if (deltaY > 0 && el.scrollTop >= max - 1) return false;
    return true;
  }
  if (!overflowAllowsScroll(style.overflowX)) return false;
  const max = el.scrollWidth - el.clientWidth;
  if (max <= 1) return false;
  if (deltaX < 0 && el.scrollLeft <= 0) return false;
  if (deltaX > 0 && el.scrollLeft >= max - 1) return false;
  return true;
}

export function useScrollContainerWheel<T extends HTMLElement>() {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((node: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!node) return;

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey) return;
      let current: HTMLElement | null = event.target as HTMLElement | null;
      while (current && current !== node) {
        if (canScrollAlong(current, event.deltaY, event.deltaX)) return;
        current = current.parentElement;
      }
      if (!canScrollAlong(node, event.deltaY, event.deltaX)) return;
      event.preventDefault();
      event.stopPropagation();
      node.scrollTop += event.deltaY;
      node.scrollLeft += event.deltaX;
    };

    node.addEventListener("wheel", onWheel, { passive: false, capture: true });
    cleanupRef.current = () => {
      node.removeEventListener("wheel", onWheel, true);
    };
  }, []);
}
